/**
 * @file 侧边导航任务列表相关
 * @author sunyingying01
 */

import React, {useCallback} from 'react';
import {observer} from 'mobx-react-lite';
import {flowResult} from 'mobx';
import {App, Menu, Popover} from 'antd';
import cx from 'classnames';
import {PlusOutlined} from '@ant-design/icons';
import {useNavigate, useLocation} from 'react-router-dom';

import {useRequestErrorHandler} from '@/utils/errorHandler';
import {withUploadGuard} from '@/components/uploadGuard';
import {JobStore, JobsListStore} from '@/stores';
import {defaultSelectJobUtil} from '@/modules/selectJobUtil';
import {jobIdSyncService} from '@/utils/JobIdSyncService';
import {useConfirmModal} from '@/components/ConfirmModal';
import {MigrationRestrictTip} from '@/modules/MigrationModules';
import {EditModal} from './EditModal';
import JobsHeader from './Header';
import JobList from './List';

import styles from './index.module.less';

export interface IProps {
    store: IPageStore;
}

const Jobs: React.FC<IProps> = observer(props => {
    const {projectsListStore, rootStore} = props.store;
    const {jobsListStore} = projectsListStore;
    const {userStore} = rootStore;
    const {modal} = App.useApp();
    const navigate = useNavigate();
    const location = useLocation();

    const {handleError} = useRequestErrorHandler();
    const {showConfirm: showDeleteConfirm} = useConfirmModal();
    const {showConfirm: showCopyConfirm} = useConfirmModal();

    // 创建任务
    const handleCreateJob = useCallback(
        () => {
             withUploadGuard(modal, () => {
                const job = new JobStore();
                job.updateShowEditModal(true);
                job.updateEditJobModalStatus('create');
                jobsListStore.setCurrentJobDetail(job);
            });
        },
        [jobsListStore, props.store.taskRunningStore]
    );

    // 重新命名
    const handleRenameJob = useCallback(
        (job: JobStore) => {
            if (userStore.isMigrationRestricted) {
                return;
            }
            withUploadGuard(modal, async () => {
                jobsListStore.setCurrentJobDetail(job);
                job.updateShowEditModal(true);
                job.updateEditJobModalStatus('update');
            });
        },
        [modal, jobsListStore, userStore]
    );

    /** 执行复制任务的核心逻辑 */
    const executeCopyJob = useCallback(
        async (job: JobStore) => {
            try {   
                // 复制任务
                await flowResult(job.fetchCopyJob(job.job_id));
                // 刷新列表
                await flowResult(jobsListStore.fetchList());
            } catch (error: any) {
                handleError(error);
            }
        },
        [jobsListStore, handleError]
    );

    // 检查是否跳过复制任务弹窗
    const checkStatusJob = useCallback(async (job: JobStore, val: boolean) => {
        try {
           await job.fetchCheckStatusJob({check_key: 'job_copy_no_prompt', status: val});
           await userStore.fetchUserInfo();
        } catch (error: any) {
            handleError(error);
        }
    }, [userStore, handleError]);

    // 复制任务
    const handleCopyJob = useCallback(
        (job: JobStore) => {
            withUploadGuard(modal, () => {
                // 如果用户设置了不再提示，直接复制任务
                if (userStore.jobCopyNoPrompt === true) {
                   executeCopyJob(job);
                   return;
                }

                // 使用局部变量保存"不再提示"的勾选状态
                let dontAskAgain = true;

                showCopyConfirm(
                    {
                        title: <span>确定创建副本？</span>,
                        content: '仅支持复制任务的原始配置，不支持复制运行结果',
                        showDontAskAgain: true,
                        dontAskAgainDefault: true,
                        onDontAskAgainChange: (checked) => {
                            dontAskAgain = checked;
                        },
                        okText: '创建',
                        successMessage: '任务复制成功！'
                    },
                    async () => {
                         // 更新确认弹窗并更新提醒勾选状态
                        checkStatusJob(job, dontAskAgain);
                        executeCopyJob(job);
                    }
                );
            });
        },
        [modal, showCopyConfirm, executeCopyJob, userStore]
    );

    /** 执行删除任务的核心逻辑 */
    const executeDeleteJob = useCallback(
        async (job: JobStore) => {
            if (userStore.isMigrationRestricted) {
                return;
            }
            const deletedJobId = job.job_id;
            const currentSelectedId = jobIdSyncService.getJobIdFromUrl();
            const isDeleteCurrentJob = (deletedJobId === currentSelectedId);
            const nextJobCandidate = jobsListStore.jobsList.find(item => item.job_id !== deletedJobId);

            // 删除当前任务前先切走 URL 和选中态，避免删除接口过程中 SiderLayout 继续按旧 URL 拉详情
            if (isDeleteCurrentJob) {
                job.update({isDeleted: true});
                job.stopAutoSave();
                props.store.taskRunningStore.stopPolling();
                props.store.taskRunningStore.cancelAllJobRequests(deletedJobId);
                props.store.taskRunningStore.resetInitializationFlag();
                props.store.taskRunningStore.initTaskRunningStore('');
                props.store.taskRunningStore.taskExecutionStore.setJobId('');
                jobsListStore.setSelectedJobId(null);
                jobsListStore.setSelectedJobDetail(new JobStore());

                if (nextJobCandidate) {
                    jobIdSyncService.setJobId(nextJobCandidate.job_id);
                } else {
                    jobIdSyncService.clearJobId();
                }
            }

            // 1. 删除任务
            await flowResult(job.fetchDeleteJob());

            // 2. 再次取消旧任务相关请求，处理删除过程中刚进入 requestManager 的请求
            if (isDeleteCurrentJob) {
                props.store.taskRunningStore.cancelAllJobRequests(deletedJobId);
            }

            // 3. 刷新列表
            await flowResult(jobsListStore.fetchList());

            // 4. 如果删除的是当前选中的任务，自动选中列表第一个任务
            const firstJob = jobsListStore.jobsList[0];
            if (firstJob && isDeleteCurrentJob) {
                jobIdSyncService.setJobId(firstJob.job_id);
                props.store.taskRunningStore.initTaskRunningStore(firstJob.job_id);
                await defaultSelectJobUtil(props.store, firstJob);
            }
        },
        [props.store, jobsListStore, handleError, location.pathname, userStore]
    );

    /** 删除任务 */
    const handleDeleteJob = useCallback(
        (job: JobStore) => {
            if (userStore.isMigrationRestricted) {
                return;
            }
            const deleteContent = (
                  <div className="delete-job-content">
                    <div className="delete-job-desc">
                        任务「{job.name}」删除后，所有数据将被
                        <span className="delete-job-highlight">永久删除且无法恢复</span>。
                    </div>
                    <div className="delete-job-subdesc">
                        系统仅扣除实际消耗的资源点，未消耗资源点将按原来源处理。
                    </div>
                    <div className="delete-job-card">
                        <div className="delete-job-card-title">以下资源点无法返还</div>
                        <div className="delete-job-card-item">1. 来自已过期流量包的资源点</div>
                        <div className="delete-job-card-item">2. 来自前一日已结算的每日额度资源点</div>
                    </div>
                </div>
            );

            withUploadGuard(modal, () => {
                showDeleteConfirm(
                    {
                        title: "确认删除任务？",
                        content: deleteContent,
                        okText: '确认删除',
                        danger: true,
                        successMessage: '任务删除成功！',
                        showDontAskAgain: false,
                        dontAskAgainDefault: false,
                        onDontAskAgainChange: undefined,
                        footerExtra: null,
                        className: 'delete-job-confirm-modal',
                        width: 480
                    },
                    async () => {
                        try {
                            await executeDeleteJob(job);
                        } catch (error: any) {
                            handleError(error);
                        }
                    }
                );
            });
        },
        [modal, showDeleteConfirm, executeDeleteJob, handleError, userStore]
    );

    // 切换选中任务 并获取任务详情, 更新列表中选中的任务相关信息
    const handleSelectJob = useCallback(
        (job: JobStore) => {
            withUploadGuard(modal, async () => {
                const {taskRunningStore} = props.store;

                /** 同步选中任务到地址栏 */
                jobIdSyncService.setJobId(job.job_id);

                // 重新初始化 taskRunningStore
                taskRunningStore.initTaskRunningStore(job.job_id);

                // 使用完整的初始化逻辑（获取 detail、设置轮询等）
                await defaultSelectJobUtil(props.store, job);

                // 非任务页主动点击任务时，需要显式跳转回任务页面，并携带当前选中的 jobId
                if (!['/index', '/taskRunning'].includes(location.pathname)) {
                    const selectedJobDetail = jobsListStore.selectedJobDetail;
                    const params = new URLSearchParams(location.search);
                    params.set('jobId', job.job_id);
                    const targetSearch = `?${params.toString()}`;
                    const targetPath = !taskRunningStore.taskExecutionStore.isInitialingPage
                        && !(selectedJobDetail instanceof JobStore && selectedJobDetail.isShowInitialingPage)
                        ? `/taskRunning${targetSearch}`
                        : `/index${targetSearch}`;
                    navigate(targetPath);
                }
            });
        },
        [modal, props.store, location.pathname, location.search, jobsListStore, navigate]
    );


    /** 受限运行期或任务数超限时，新任务入口置灰 */
    const addJobDisabled = jobsListStore.checkExceededlimit || userStore.isMigrationRestricted;

    const addJobContent = (
        <>
            <div className={styles.icon}>
                <PlusOutlined />
            </div>
            <div className={styles.title}>
                新任务
            </div>
        </>
    );

    /** 新任务入口：受限运行期引导到新平台，超限时提示升级 */
    const renderAddJobBtn = () => {
        if (userStore.isMigrationRestricted) {
            return (
                <MigrationRestrictTip enabled>
                    <div className={cx(styles.addJobBtn, styles.disabledAddJobBtn)}>
                        {addJobContent}
                    </div>
                </MigrationRestrictTip>
            );
        }

        if (jobsListStore.checkExceededlimit) {
            const planText = userStore.subscriptionPlanText;
            const limit = JobsListStore.LimitNumber;
            const isPro = userStore.subscriptionPlan?.toLowerCase() === 'pro';

            return (
                <Popover
                    classNames={{root: styles.popoverWrapper}}
                    content={`${planText}账号仅支持创建${limit}个任务，请升级${isPro ? '企业版' : '进阶版'}账号。`}
                    trigger={['hover']}
                    placement="right"
                >
                    <div className={cx(styles.addJobBtn, styles.disabledAddJobBtn)}>
                        {addJobContent}
                    </div>
                </Popover>
            );
        }

        return (
            <div className={styles.addJobBtn} onClick={handleCreateJob}>
                {addJobContent}
            </div>
        );
    };

    return (
        <>
            <JobsHeader store={props.store} />
            <div className={styles.jobWrapper}>
                <Menu.Item
                    key="jobs"
                    className={cx(styles.addJobMenu, {[styles.disabledAddJobMenu]: addJobDisabled})}
                    disabled={addJobDisabled}
                >
                    {renderAddJobBtn()}
                </Menu.Item>
            </div>
            <EditModal
                store={props.store}
                jobStore={props.store.projectsListStore.jobsListStore.currentJobDetail}
            />
            <JobList
                store={props.store.projectsListStore.jobsListStore}
                handleRenameJob={handleRenameJob}
                handleCopyJob={handleCopyJob}
                handleDeleteJob={handleDeleteJob}
                handleSelectJob={handleSelectJob}
                userStore={userStore}
            />
        </>
    );
});

export default Jobs;
